import { Component, ComponentInterface, Element, Event, EventEmitter, h, Host, Listen, Prop, State, Watch } from "@stencil/core";
import { HTMLListItem, SelectionBehavior } from "../../utils/behaviors/interfaces";
import { MultiSelectionBehavior } from "../../utils/behaviors/multi-selection";
import { watchForOptions } from "../../utils/watcher";

@Component({
    tag: "uic-check-list",
    styleUrl: "check-list.scss"
})
export class CheckList implements ComponentInterface {
    @Element() host: HTMLUicCheckListElement;

    @State()
    hasFocus = false;

    private selectionBehavior: SelectionBehavior = this.createMultiSelectionBehavior();
    private observer: MutationObserver;
    /**
     * The list of selected values.
     */
    @Prop({ mutable: true }) selectedValues: Array<string> = [];

    /**
     * Emitted when the list of seleted values change.
     */
    @Event({ eventName: "uicChange" }) change: EventEmitter<string[]>;

    @Listen("uicChange")
    cancelChildEvent(event: CustomEvent) {
        if (event.target !== this.host) {
            event.stopPropagation();
        }
    }

    @Watch("selectedValues")
    onValueChanged() {
        this.selectionBehavior.setValue(this.selectedValues);
        this.change.emit(this.selectedValues);
    }

    connectedCallback() {
        this.observer = watchForOptions(this.host, "[uic-item]", () => this.selectionBehavior.setValue(this.selectedValues));
    }

    componentDidLoad() {
        this.selectionBehavior.setValue(this.selectedValues);

        const list = this.getList();
        if (list) {
            setTimeout(() => list.setFocus());
        }
    }

    disconnectedCallback() {
        this.observer.disconnect();
        this.observer = null;
    }

    private createMultiSelectionBehavior() {
        return new MultiSelectionBehavior({
            getItems: this.getItems.bind(this),
            getSelectedItem: this.getSelectedItem.bind(this),
            notifySelect: () => {},
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            setValue: (value: any) => {
                this.selectedValues = value;
            }
        });
    }

    private handleSelect(event: CustomEvent) {
        const target = event.detail || event.target;
        this.selectionBehavior.select(target);
    }

    private getSelectedItem() {
        return this.host.querySelector<HTMLElement>("[uic-item][selected]");
    }

    private getItems() {
        return [...this.host.querySelectorAll<HTMLListItem>("[uic-item]")];
    }

    private getList() {
        return this.host.querySelector("uic-list");
    }

    render() {
        return (
            <Host>
                <uic-list onUicSelect={(e) => this.handleSelect(e)}>
                    <uic-scrollpanel class='uic-check-list__scrollpanel'>
                        <slot></slot>
                    </uic-scrollpanel>
                </uic-list>
            </Host>
        );
    }
}
